import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography } from '../../common/Typography';
import { interactiveBleedInsetX } from '../../../constants/interactiveSafeInsets';
import { ClubOverviewSection } from './ClubOverviewSection';
import { UpcomingSessionCard } from './ClubUpcomingSessionsCarousel';
import { RecentSessionCard } from './ClubRecentSessionsCarousel';

const carouselClass = `flex items-stretch gap-3 overflow-x-auto pb-2 pt-0.5 ${interactiveBleedInsetX} scroll-smooth snap-x snap-mandatory sm:snap-none [scrollbar-gutter:stable_both-edges] touch-pan-x scrollbar-hide-mobile`;

function sortUpcomingSessions(sessions) {
  return [...sessions].sort((a, b) => {
    const aLive = a.session_status === 'ongoing' ? 0 : 1;
    const bLive = b.session_status === 'ongoing' ? 0 : 1;
    if (aLive !== bLive) return aLive - bLive;
    const aDate = a.session_date ? new Date(a.session_date).getTime() : 0;
    const bDate = b.session_date ? new Date(b.session_date).getTime() : 0;
    return aDate - bDate;
  });
}

function sortRecentSessions(sessions) {
  return [...sessions].sort((a, b) => {
    const aDate = a.session_date ? new Date(a.session_date).getTime() : 0;
    const bDate = b.session_date ? new Date(b.session_date).getTime() : 0;
    return bDate - aDate;
  });
}

function sliceSessions(sessions, maxItems) {
  if (maxItems == null) return sessions;
  return sessions.slice(0, maxItems);
}

/** Vertical divider between the two session groups in the horizontal strip. */
export function SessionTimelineDivider({ label = 'Past' }) {
  return (
    <div
      className="shrink-0 snap-start flex flex-col items-center self-stretch px-1 w-11 sm:w-12"
      role="separator"
      aria-label={`${label} sessions`}
    >
      <div className="flex flex-col items-center gap-1.5 h-full py-0.5">
        <div className="w-px flex-1 min-h-[1.25rem] bg-border-medium" />
        <span className="text-[9px] font-bold uppercase tracking-widest text-text-muted [writing-mode:vertical-rl] rotate-180 select-none">
          {label}
        </span>
        <div className="w-px flex-1 min-h-[1.25rem] bg-border-medium" />
      </div>
    </div>
  );
}

/**
 * Horizontal sessions strip.
 * Default: live/upcoming, optional divider, then recent.
 * pastFirst: last played first, optional divider, then upcoming.
 */
export function ClubSessionsCarouselStrip({
  upcomingSessions = [],
  recentSessions = [],
  currentUser,
  clubId,
  canManage,
  sessionWinRates,
  onRefresh,
  onViewSessionPerformance,
  maxItems = 5,
  compact = false,
  pastFirst = false,
}) {
  const upcoming = useMemo(
    () => sliceSessions(sortUpcomingSessions(upcomingSessions), maxItems),
    [upcomingSessions, maxItems]
  );
  const recent = useMemo(
    () => sliceSessions(sortRecentSessions(recentSessions ?? []), maxItems),
    [recentSessions, maxItems]
  );

  const hasUpcoming = upcoming.length > 0;
  const hasRecent = recent.length > 0;

  if (!hasUpcoming && !hasRecent) return null;

  const upcomingCards = upcoming.map((session) => (
    <UpcomingSessionCard
      key={`upcoming-${session.id}`}
      session={session}
      currentUser={currentUser}
      clubId={clubId}
      canManage={canManage}
      onRefresh={onRefresh}
      compact={compact}
    />
  ));
  const recentCards = recent.map((session) => (
    <RecentSessionCard
      key={`recent-${session.id}`}
      session={session}
      currentUser={currentUser}
      sessionWinRates={sessionWinRates}
      onViewSessionPerformance={onViewSessionPerformance}
      compact={compact}
    />
  ));

  const showDivider = hasUpcoming && hasRecent;
  const dividerLabel = pastFirst ? 'Upcoming' : 'Past';

  return (
    <div role="region" aria-label="Club sessions" className={carouselClass}>
      {pastFirst ? recentCards : upcomingCards}
      {showDivider && <SessionTimelineDivider label={dividerLabel} />}
      {pastFirst ? upcomingCards : recentCards}
    </div>
  );
}

/**
 * Unified sessions strip — one overview section.
 * Default order: live & upcoming, divider, then recent.
 * pastFirst: last played, divider, then upcoming.
 *
 * @param {boolean} [embedded=false] - Carousel only (no ClubOverviewSection shell).
 * @param {number|null} [maxItems=5] - Max cards per group; null shows all.
 * @param {boolean} [pastFirst=false] - Lead with the most recently played session.
 */
export function ClubSessionsOverviewCarousel({
  upcomingSessions = [],
  recentSessions = [],
  currentUser,
  clubId,
  canManage,
  sessionWinRates,
  onRefresh,
  onViewSessionPerformance,
  onViewAll,
  onSectionClick,
  embedded = false,
  maxItems = 5,
  pastFirst = false,
}) {
  const navigate = useNavigate();

  const handleViewAll = onViewAll ?? (() => navigate(`/clubs/${clubId}?tab=club`));
  const handleSectionNav = onSectionClick ?? handleViewAll;

  const upcoming = useMemo(
    () => sliceSessions(sortUpcomingSessions(upcomingSessions), maxItems),
    [upcomingSessions, maxItems]
  );
  const recent = useMemo(
    () => sliceSessions(sortRecentSessions(recentSessions ?? []), maxItems),
    [recentSessions, maxItems]
  );

  const hasUpcoming = upcoming.length > 0;
  const hasRecent = recent.length > 0;

  const stripProps = {
    upcomingSessions,
    recentSessions,
    currentUser,
    clubId,
    canManage,
    sessionWinRates,
    onRefresh,
    onViewSessionPerformance,
    maxItems,
    compact: !embedded,
    pastFirst,
  };

  if (!hasUpcoming && !hasRecent) {
    if (embedded) {
      return (
        <Typography variant="muted" className="text-center py-6 block">
          No upcoming or recent sessions.
        </Typography>
      );
    }

    return (
      <ClubOverviewSection
        label="Sessions"
        variant="sessions"
        subtitle="No sessions at this club yet"
        onViewAll={handleViewAll}
        onSectionClick={handleSectionNav}
      >
        <div className="text-center py-6 rounded-xl border border-border-medium bg-surface-subtle/60">
          <Typography variant="muted">No upcoming or recent sessions.</Typography>
        </div>
      </ClubOverviewSection>
    );
  }

  if (embedded) {
    return <ClubSessionsCarouselStrip {...stripProps} />;
  }

  return (
    <ClubOverviewSection
      label="Sessions"
      variant="sessions"
      subtitle=""
      onViewAll={handleViewAll}
      onSectionClick={handleSectionNav}
    >
      <div onClick={(e) => e.stopPropagation()}>
        <ClubSessionsCarouselStrip {...stripProps} />
      </div>
    </ClubOverviewSection>
  );
}
